import React, { useState, useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { organizationService } from "../../../services/api/organization";
import { documentService } from "../../../services/api/documentService";
import { toast } from "sonner";
import Button2 from "../../../components/elements/forms/Button2";
import PdfPreviewModal from "../../../components/Modal/PdfPreviewModal";
import StatusModal from "../../../components/Modal/StatusModal";
import OnboardingFormContent, {
  OnboardingFormData,
} from "./OnboardingFormContent";
import { format } from "date-fns";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import axiosInstance from "../../../utility/axiosInstance";
import TextInputBox from "../../../components/elements/forms/TextInputBox";

const initialFormData: OnboardingFormData = {
  clientName: "",
  dateSubmitted: format(new Date(), "yyyy-MM-dd"),
  includeEmployeeLifecycle: false,

  pilotCompaniesConfirmed: false,
  numberOfPilotCompanies: "",
  // pilotCompanyNames: ["", "", "", ""],
  // legalEntityNumbers: ["", "", "", ""],
  childOrganizations: [{ name: "", domain: "", contactEmail: "" }],

  legalCompanyName: "",
  brandDisplayName: "",
  headOfficeAddress: "",
  industrySector: "",
  estimatedEmployeeCount: "",
  parentDomain: "",
  crossBorderProcessing: false,
  crossBorderJurisdictions: "",
  NIN: "",
  TIN: "",
  cacRegNumber: "",

  totalPhase1Users: "",
  roleBasedAccessRequired: false,
  roleBasedAccessDetails: "",
  thirdPartyAccessDeclared: false,
  thirdPartyAccessDetails: "",

  billingContactName: "",
  billingContactEmail: "",
  billingContactPhone: "",
  preferredInvoicingEmail: "",
  paymentMethodPreference: "",
  specialInvoicingInstructions: "",

  personnel: [
    {
      jobTitle: "Business Owner",
      firstName: "",
      lastName: "",
      email: "",
      accessLevel: 1,
    },
    {
      jobTitle: "Project Manager",
      firstName: "",
      lastName: "",
      email: "",
      accessLevel: 2,
    },
  ],

  existingHRIS: "",
  hrisIntegrationRequired: false,
  hrisIntegrationDetails: "",
  jobPostingPlatforms: "",
  emailProvider: "",
  smsGatewayProvider: "",
  biometricIntegrationRequired: false,
  biometricDetails: "",

  platformDisplayName: "",
  primaryBrandColor: "",
  customDomain: "",
  complianceRules: "",

  additionalDeploymentNotes: "",

  scopeConfirmed: false,
  expansionAcknowledged: false,
  unauthorizedBreachAcknowledged: false,
  dataComplianceConfirmed: false,
  electronicAgreement: false,

  authorizedFullName: "",
  authorizedTitle: "",
  authorizedDate: format(new Date(), "yyyy-MM-dd"),
};

const OnboardingFormOld: React.FC = () => {
  const [searchParams] = useSearchParams();
  const id = searchParams.get("Id");
  const formContentRef = useRef<HTMLDivElement>(null);

  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState<OnboardingFormData>(initialFormData);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [previewModal, setPreviewModal] = useState(false);
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const [cacFile, setCacFile] = useState<File | null>(null);
  const [tinFile, setTinFile] = useState<File | null>(null);
  const [ninFile, setNinFile] = useState<File | null>(null);
  const [companyProfileFile, setCompanyProfileFile] = useState<File | null>(
    null,
  );
  const [statusModal, setStatusModal] = useState<{
    isOpen: boolean;
    status: "success" | "error";
    message: string;
  }>({
    isOpen: false,
    status: "success",
    message: "",
  });

  // suggestions / feedbacks
  const [showSuggestion, setShowSuggestion] = useState(false);
  const [suggestion, setSuggestion] = useState("");
  const [suggestionAmount, setSuggestionAmount] = useState("");
  const [suggestionFile, setSuggestionFile] = useState<File | null>(null);
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);

  useEffect(() => {
    const fetchOrg = async () => {
      if (!id) {
        setLoading(false);
        return;
      }
      try {
        const response = await organizationService.get(id);
        if (response?.data?.name) {
          setFormData((prev) => ({
            ...prev,
            clientName: response.data.name,
          }));
        }
      } catch (error) {
        console.error("Failed to fetch organization details", error);
        toast.error("Could not load organization details.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrg();
  }, [id]);

  // Cleanup logo blob URL on unmount
  useEffect(() => {
    return () => {
      if (logoPreview) {
        URL.revokeObjectURL(logoPreview);
      }
    };
  }, [logoPreview]);

  const handleFormChange = (field: keyof OnboardingFormData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleChildOrgChange = (
    index: number,
    field: keyof OnboardingFormData["childOrganizations"][0],
    value: string,
  ) => {
    setFormData((prev) => {
      const newChildOrgs = [...prev.childOrganizations];
      newChildOrgs[index] = { ...newChildOrgs[index], [field]: value };
      return { ...prev, childOrganizations: newChildOrgs };
    });
  };

  const addChildOrg = () => {
    setFormData((prev) => ({
      ...prev,
      childOrganizations: [
        ...prev.childOrganizations,
        { name: "", domain: "", contactEmail: "" },
      ],
    }));
  };

  const removeChildOrg = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      childOrganizations: prev.childOrganizations.filter((_, i) => i !== index),
    }));
  };

  const handleStakeholderChange = (
    index: number,
    field: keyof OnboardingFormData["personnel"][0],
    value: any,
  ) => {
    setFormData((prev) => {
      const newPersonnel = [...prev.personnel];
      newPersonnel[index] = { ...newPersonnel[index], [field]: value };
      return { ...prev, personnel: newPersonnel };
    });
  };

  const addStakeholder = () => {
    setFormData((prev) => ({
      ...prev,
      personnel: [
        ...prev.personnel,
        {
          jobTitle: "",
          firstName: "",
          lastName: "",
          email: "",
          accessLevel: 1,
        },
      ],
    }));
  };

  const removeStakeholder = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      personnel: prev.personnel.filter((_, i) => i !== index),
    }));
  };

  const handleLogoUpload = (file: File) => {
    const allowedTypes = ["image/jpeg", "image/jpg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, and PNG files are allowed.");
      return;
    }
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const handleRemoveLogo = () => {
    if (logoPreview) {
      URL.revokeObjectURL(logoPreview);
    }
    setLogoFile(null);
    setLogoPreview(null);
  };

  const handleCacUpload = (file: File) => setCacFile(file);
  const handleTinUpload = (file: File) => setTinFile(file);
  const handleNinUpload = (file: File) => setNinFile(file);
  const handleCompanyProfileUpload = (file: File) =>
    setCompanyProfileFile(file);

  const validateForm = (): boolean => {
    if (!formData.clientName.trim()) {
      toast.error("Please enter the client/company name");
      return false;
    }
    if (!formData.authorizedFullName.trim()) {
      toast.error("Please enter the authorized signatory name");
      return false;
    }
    if (!formData.billingContactName.trim()) {
      toast.error("Please enter the billing name");
      return false;
    }
    if (!formData.billingContactPhone.trim()) {
      toast.error("Please enter the billing phone");
      return false;
    }
    if (!formData.billingContactEmail.trim()) {
      toast.error("Please enter the billing email");
      return false;
    }
    // if (!formData.projectSponsor.trim()) {
    //   toast.error("Please enter the project sponsor");
    //   return false;
    // }
    // if (!formData.financeLead.trim()) {
    //   toast.error("Please enter the finance lead");
    //   return false;
    // }
    // if (!formData.hrLead.trim()) {
    //   toast.error("Please enter the HR lead");
    //   return false;
    // }
    // if (!formData.itLead.trim()) {
    //   toast.error("Please enter the IT lead");
    //   return false;
    // }
    if (!formData.electronicAgreement) {
      toast.error("Please confirm electronic agreement to onboarding terms");
      return false;
    }

    // New Fields Validation
    // if (!formData.cacRegNumber.trim()) {
    //   toast.error("Please enter the CAC Registration Number");
    //   return false;
    // }
    // if (!formData.TIN.trim()) {
    //   toast.error("Please enter the TIN Number");
    //   return false;
    // }
    // if (!formData.NIN.trim()) {
    //   toast.error("Please enter the NIN Number");
    //   return false;
    // }

    // File Validation
    if (!cacFile) {
      toast.error("Please upload the CAC Document");
      return false;
    }
    // if (!tinFile) {
    //   toast.error("Please upload the TIN Document");
    //   return false;
    // }
    // if (!ninFile) {
    //   toast.error("Please upload the NIN Document");
    //   return false;
    // }

    return true;
  };

  const handlePreview = async () => {
    if (!id) {
      toast.error("Organization ID is missing.");
      return;
    }

    if (!validateForm()) return;

    // Open modal immediately to show loading state
    setPreviewModal(true);
    setPdfBlobUrl(null);
    setPdfFile(null);

    try {
      const contentEl = formContentRef.current;
      if (!contentEl) {
        toast.error("Unable to capture form content.");
        setPreviewModal(false);
        return;
      }

      const canvas = await html2canvas(contentEl, {
        scale: 1.5, // Reduced from 2 for compression
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/jpeg", 0.75); // Using JPEG with compression 0.75
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = pdfWidth / imgWidth;
      const scaledHeight = imgHeight * ratio;

      let currentPosition = 0;

      while (currentPosition < scaledHeight) {
        if (currentPosition > 0) {
          pdf.addPage();
        }
        pdf.addImage(
          imgData,
          "JPEG",
          0,
          -currentPosition,
          pdfWidth,
          scaledHeight,
          undefined,
          "FAST",
        );
        currentPosition += pdfHeight;
      }

      const pdfBlob = pdf.output("blob");
      const file = new File(
        [pdfBlob],
        `onboarding_form_${formData.clientName.replace(/\s+/g, "_")}.pdf`,
        { type: "application/pdf" },
      );

      setPdfFile(file);
      setPdfBlobUrl(URL.createObjectURL(pdfBlob));
    } catch (error) {
      console.error("Error generating PDF preview:", error);
      toast.error("Failed to generate PDF preview.");
      setPreviewModal(false);
    }
  };

  const handleSubmit = async () => {
    if (!pdfFile || !id) return;

    setIsSubmitting(true);
    try {
      // 1. Submit Access Details with Files
      await organizationService.submitAccessDetails(id, {
        ...formData,
        cacFile,
        tinFile,
        ninFile,
        companyProfileFile,
      });

      // 2. Upload PDF Onboarding Form
      const response = await documentService.uploadDocument(
        id,
        "ONBOARDING_FORM",
        pdfFile,
      );

      setPreviewModal(false);
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
      setPdfBlobUrl(null);
      setPdfFile(null);

      setStatusModal({
        isOpen: true,
        status: "success",
        message: response.message || "Onboarding form submitted!",
      });
    } catch (error: any) {
      console.error("Error submitting onboarding form:", error);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to submit onboarding form.";
      setStatusModal({
        isOpen: true,
        status: "error",
        message: errorMessage,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClosePreview = () => {
    if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    setPdfBlobUrl(null);
    setPdfFile(null);
    setPreviewModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const handleSubmitSuggestion = async () => {
    if (!suggestion.trim()) {
      toast.error("Please enter a suggestion");
      return;
    }

    try {
      setSubmittingSuggestion(true);

      const suggestionFormData = new FormData();
      suggestionFormData.append("suggestion", suggestion);
      // if (suggestionAmount) {
      //   suggestionFormData.append("amount", suggestionAmount);
      // }
      if (suggestionFile) {
        suggestionFormData.append("upload", suggestionFile);
      }

      await axiosInstance.post(
        `/organizations/${id}/suggestions`,
        suggestionFormData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        },
      );
      toast.success("Thank you for your suggestion!");
      setSuggestion("");
      // setSuggestionAmount("");
      setSuggestionFile(null);
      setShowSuggestion(false);
    } catch (error) {
      console.error("Error submitting suggestion:", error);
      toast.error("Failed to submit suggestion");
    } finally {
      setSubmittingSuggestion(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 font-abel">
      <div className="max-w-[1100px] mx-auto px-4">
        {/* Page Header */}
        <div className="mb-6 text-center">
          <h1 className="text-3xl font-bold text-gray-900">
            Onboarding & Pre Go-Live Setup
          </h1>
          <p className="text-gray-500 mt-2">
            Complete this checklist to initiate your 25th Suite deployment
          </p>
        </div>

        {/* Form Content (captured for PDF) */}
        <div ref={formContentRef}>
          <OnboardingFormContent
            formData={formData}
            onFormChange={handleFormChange}
            onChildOrgChange={handleChildOrgChange}
            addChildOrg={addChildOrg}
            removeChildOrg={removeChildOrg}
            onStakeholderChange={handleStakeholderChange}
            addStakeholder={addStakeholder}
            removeStakeholder={removeStakeholder}
            logoFile={logoFile}
            logoPreview={logoPreview}
            onLogoUpload={handleLogoUpload}
            onRemoveLogo={handleRemoveLogo}
            cacFile={cacFile}
            tinFile={tinFile}
            ninFile={ninFile}
            onCacUpload={handleCacUpload}
            onTinUpload={handleTinUpload}
            onNinUpload={handleNinUpload}
            companyProfileFile={companyProfileFile}
            onCompanyProfileUpload={handleCompanyProfileUpload}
          />
        </div>

        <div className="max-w-5xl mx-auto mt-3">
          <button
            onClick={() => setShowSuggestion(!showSuggestion)}
            className="text-black text-[16px] font-medium hover:underline flex items-center justify-center gap-1"
          >
            Need a different custom requirement? Click here
          </button>

          {showSuggestion && (
            <div className="mt-2 p-6 bg-white rounded-[15px] border border-gray-200">
              <TextInputBox
                label="Custom Requirements"
                placeholder="Tell us what custom requirement you have..."
                name="suggestion"
                value={suggestion}
                onChange={(e) => setSuggestion(e.target.value)}
                rows={4}
              />
              <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    value={suggestionAmount}
                    onChange={(e) => setSuggestionAmount(e.target.value)}
                    placeholder="E.g., 78"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors"
                  />
                </div> */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Upload File (Optional)
                  </label>
                  <input
                    type="file"
                    onChange={(e) =>
                      setSuggestionFile(e.target.files?.[0] || null)
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary transition-colors file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-white hover:file:bg-primary/90"
                  />
                </div>
              </div>
              <div className="flex justify-start mt-4">
                <button
                  onClick={handleSubmitSuggestion}
                  disabled={submittingSuggestion}
                  className={`py-2 px-6 rounded-lg text-white font-medium transition-all ${
                    submittingSuggestion
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-primary hover:bg-blue-700"
                  }`}
                >
                  {submittingSuggestion
                    ? "Submitting..."
                    : "Submit Custom Requirement"}
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Preview Button */}
        <div className="mt-8 flex justify-center pb-12">
          <Button2
            title="Preview Form"
            onSubmitHandler={handlePreview}
            className="px-10 py-3 rounded-md text-white font-medium transition-colors text-base bg-primary hover:bg-primary/90"
          />
        </div>
      </div>

      <PdfPreviewModal
        isOpen={previewModal}
        onClose={handleClosePreview}
        pdfUrl={pdfBlobUrl}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        title="Onboarding Form Preview"
        submitButtonText="Submit Form"
      />

      <StatusModal
        isOpen={statusModal.isOpen}
        status={statusModal.status}
        message={statusModal.message}
        onClose={() => setStatusModal((prev) => ({ ...prev, isOpen: false }))}
        onAction={
          statusModal.status === "success"
            ? () => {
                const id = searchParams.get("Id");
                const search = new URLSearchParams();
                if (id) search.set("Id", id);
                // Keep other params if they exist, but at least Id is needed
                window.location.href = `/pricing?${search.toString()}`;
              }
            : undefined
        }
      />
    </div>
  );
};

export default OnboardingFormOld;
